import React from "react";
import styled, { useTheme } from "styled-components";
import { Typography } from "../../../general/Typography";
import { XPBadge } from "../XPBadge";
import { CheckCircle, ThumbsDown } from "lucide-react";

export interface QuizResultBadgeProps {
  score: number;
  total: number;
  xpAwarded?: number;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.xxl} ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.backgroundHover};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: ${({ theme }) => theme.elevations.high};
  transition: all 0.3s ease-in-out;
  animation: popIn 0.4s ease;

  @keyframes popIn {
    0% {
      transform: scale(0.95);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

export const QuizResultBadge: React.FC<QuizResultBadgeProps> = ({
  score,
  total,
  xpAwarded = 50,
}) => {
  const theme = useTheme();
  const passed = score / total >= 0.6;

  return (
    <Wrapper>
      {passed ? (
        <CheckCircle size={48} color={theme.colors.success} />
      ) : (
        <ThumbsDown size={48} color={theme.colors.warning} />
      )}
      <Typography variant="heading3">
        {passed ? "🎉 Great job!" : "💡 Keep practicing!"}
      </Typography>
      <Typography variant="body">
        You scored <strong>{score}</strong> out of <strong>{total}</strong>
      </Typography>
      {passed && (
        <>
          <XPBadge xp={xpAwarded} />
          <Typography variant="caption" style={{ marginTop: 4 }}>
            +{xpAwarded} XP earned!
          </Typography>
        </>
      )}
    </Wrapper>
  );
};
